import { Link } from "react-router-dom";
import { BadgeCheck, MapPin, Store } from "lucide-react";
import { useMall } from "@/context/MallContext";
import { DEFAULT_THEME, themeOf, tint } from "@/lib/mall";

const TIER_LABEL = { kiosk: "Starter", storefront: "Storefront", department: "Pro Shop" };

const honors = (store) => Boolean(store?.crew_perks ?? store?.crew_perks_honored ?? false);

/** Some shops set their pickup line to just their city — don't print it twice. */
const fulfillment = (store) => {
    const line = (store?.fulfillment_line || "").trim();
    return line && line.toLowerCase() !== (store?.city || "").trim().toLowerCase() ? line : "";
};

/** Chip shown on stores that opted into honoring Gang member discounts. */
export function GangHonoredBadge({ className = "", testid = "gang-honored-badge" }) {
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border border-brand-green/40 bg-brand-green/10 px-2 py-0.5 font-mono text-[11px] uppercase tracking-[0.16em] text-brand-green ${className}`}
            data-testid={testid}>
            <BadgeCheck size={10} /> Gang prices honored
        </span>
    );
}

/** The shop you're standing in gets its own paint; every other shop keeps the house green. */
function paintFor(store, active) {
    return active?.slug && store?.slug === active.slug ? themeOf(active) : DEFAULT_THEME;
}

/** Who is selling this — on every product, sign, class and classified card. */
export function SoldBy({ store, className = "", withFulfillment = true, testid }) {
    const { store: active } = useMall();
    if (!store) return null;
    const t = paintFor(store, active);
    return (
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase tracking-[0.14em] ${className}`} data-testid={testid || `sold-by-${store.slug}`}>
            <span className="inline-flex items-center gap-1 text-zinc-400">
                {store.verified ? <BadgeCheck size={11} style={{ color: t.ink || t.primary }} /> : <Store size={11} className="text-brand-blue" />}
                <span className="text-zinc-300">{store.name}</span>
            </span>
            {store.city && <span className="inline-flex items-center gap-1 text-zinc-500"><MapPin size={10} /> {store.city}</span>}
            {withFulfillment && fulfillment(store) && <span style={{ color: t.ink || t.primary }}>{fulfillment(store)}</span>}
            {honors(store) && <GangHonoredBadge testid={`gang-honored-${store.slug}`} />}
        </div>
    );
}

/** Bigger banner used on product pages and designers so the shopper always knows the store. */
export function StoreContextBar({ store, note, className = "" }) {
    const { store: active } = useMall();
    if (!store) return null;
    const t = paintFor(store, active);
    return (
        <Link to={`/stores/${store.slug}`}
            className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-2xl border px-4 py-2.5 transition-colors ${className}`}
            style={{ borderColor: tint(t.primary, 0.35), background: tint(t.primary, 0.06) }}
            data-testid="store-context-bar">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">Shopping from</span>
            <span className="inline-flex items-center gap-1.5 font-display text-sm font-bold text-white">
                {store.verified && <BadgeCheck size={14} style={{ color: t.ink || t.primary }} />}{store.name}
            </span>
            {store.city && <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">{store.city}</span>}
            {fulfillment(store) && <span className="font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: t.ink || t.primary }}>{fulfillment(store)}</span>}
            {note && <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">{note}</span>}
            {honors(store) && <GangHonoredBadge />}
            <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: t.ink || t.primary }}>
                {TIER_LABEL[store.tier] || "Store"} →
            </span>
        </Link>
    );
}

